import React, { useState, useEffect, useContext, useRef } from 'react';
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Accordion from 'react-bootstrap/Accordion';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import * as dayjs from 'dayjs';
import { getSelectedFarmId, getSelectedLang, getSelectedYear } from '../../common';
import { groupByKey } from '../../commonFunctions';
import Spinner from 'react-bootstrap/Spinner';
import { ICONS } from '../../img/icons'
import Icon from '../../img/Icon'
import '../../css/general.css'
import '../../css/assignment.css'
import { postContentWithAction, getContentWithAction, getContent } from '../../apiConnect';
import ActionSelectionModal from './ActionSelectionModal';
import MapModal from './MapModal';
import AppContext from '../../AppContext';
import { CultivationType } from '../../Constants/CultivationType';
import ScrollableFX from '../ComponentStyles/ScrollableFX';

const AssignmentsModal = (props) => {
    const { t } = useTranslation();
    const { auth } = useContext(AppContext);
    const [loading, setLoading] = useState(true);
    const [plots, setPlots] = useState([]);
    const [farmAssignments, setFarmAssignments] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [checkedPlots, setCheckedPlots] = useState([]);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [isUserEmployee, setIsUserEmployee] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [showActionModal, setShowActionModal] = useState(false);
    const [buttonWriteShow, setButtonWriteShow] = useState(false); // allowed save btn disabled
    const [mapModalShow, setMapModalShow] = useState(false); // show map modal

    const [refresh, setRefresh] = useState(false); // refresh assignments
    const [scrollFx, setScrollFx] = useState(null);
    const modalBody = useRef(null);

    useEffect(() => {
        loadModalData();
    }, []);

    const loadModalData = () => {
        var farmId = getSelectedFarmId();
        var farmYear = getSelectedYear();

        setLoading(true);

        Promise.all([
            getContent(auth.getAccessToken(), 'farmPlot', { lang: getSelectedLang() }),
            getContentWithAction(auth.getAccessToken(), 'assignments', 'getEmployees', { farmId }),
            getContentWithAction(auth.getAccessToken(), 'assignments', 'getAssignments', { farmId, farmYear }),
            getContentWithAction(auth.getAccessToken(), 'session', 'storeAllowed', {}),
            getContentWithAction(auth.getAccessToken(), 'session', 'getUserInfo', {}),
            getContentWithAction(auth.getAccessToken(), 'assignments', 'getEmployeeStatus', {})
        ]).then(([plots, employees, assignments, session, userInfo, isEmployee]) => {
            // Check if user has write permissions
            setButtonWriteShow(session);

            // if user is employee, set global variable true and list of employees
            if (isEmployee) {
                setIsUserEmployee(true);
            }
            else {
                setIsUserEmployee(false);

                // Set default value for Select
                var selectDefault = { value: 'all', label: t('ShowAll')};
                employees.unshift(selectDefault);
                setSelectedEmployee(selectDefault);
                
                setEmployees(employees);
            }

            var user = userInfo;
            handleAssignments(user, isEmployee, assignments);

            setPlots(plots);
            setLoading(false);
            setRefresh(false);
            setScrollFx(true);
        });

        setCheckedPlots([]);
        setSelectedAssignments([]);
    }

    /**
     * Handles assignments for the selected user. 
     * If the selected user is an employee, it filters the assignments for that user. 
     * If not, it shows all assignments.
     * @param {any} selectedUser Selected user. Default is the logged in user.
     * @param {any} isEmployee Is selected user an employee.
     * @param {any} assignments List of farm assignments
     */
    const handleAssignments = (selectedUser, isEmployee, assignments) => {
        if (assignments.length <= 0) {
            return;
        }

        setFarmAssignments(assignments);
        var filteredAssignments = [];

        if (isEmployee) {
            filteredAssignments = assignments.filter(a => a.appUserId === selectedUser).map(a => ({ ...a }));
        }
        else {
            filteredAssignments = assignments.map(a => ({...a}));
        }

        // Add plot data to assignments
        filteredAssignments.forEach(a => {
            a.plotData = plots.find(p => p.apNumber === a.plotId);
        });
        // Sort assignments by start date
        filteredAssignments.sort((a, b) => a.startDate > b.startDate ? 1 : -1);

        // Find today's assignment and place it on top of the list
        var today = dayjs().format('YYYY-MM-DD');
        var todayAssignments = filteredAssignments.filter(a => dayjs(a.startDate).format("YYYY-MM-DD") === today && !a.completed);
        var otherAssignments = filteredAssignments.filter(a => dayjs(a.startDate).format("YYYY-MM-DD") !== today && !a.completed);
        filteredAssignments = todayAssignments.concat(otherAssignments);

        // Sort assignments by start date
        setAssignments(groupByKey(filteredAssignments, 'startDate'));
    }

    const handleCheckboxSelection = (p, data) => {
        var plotAssignments = data.assignments.filter(a => a.plotId === p.apNumber);

        if (checkedPlots.find(c => c.rowId === p.rowId)) {
            // If plot is already selected, remove it from the list
            var checked = [...checkedPlots.filter(c => c.rowId !== p.rowId)];
            setCheckedPlots(checked);

            var selAssignments = [...selectedAssignments];
            // Filter out assignments that are not in the selected plots list
            selAssignments = selAssignments.filter(a => !plotAssignments.some(pa => pa.id === a.id));
            setSelectedAssignments(selAssignments);
        }
        else {
            // Add plot to the list of selected plots
            var checked = [...checkedPlots];
            checked.push({ rowId: p.rowId, plotId: p.apNumber });
            setCheckedPlots(checked);

            var selAssignments = [...selectedAssignments];
            // Add assignments to the selected plots list
            selAssignments.push(...plotAssignments);
            setSelectedAssignments(selAssignments);
        }
    }

    const AssignmentDateRow = (data) => {
        var plotIds = data.assignments.map(a => a.plotId);
        // Filter only unique plotIds
        plotIds = [...new Set(plotIds)];
        // Create a filtered list of plots and make a true copy (instead of reference copy)
        var plotData = plots.filter(p => plotIds.includes(p.apNumber)).map(p => ({...p}));
        plotData.forEach(p => {
            var op = data.assignments.find(a => a.plotId === p.apNumber);
            // Create a unique rowId for plots for easier handling of selected plots
            p.rowId = op.plotId + op.startDate;
        });

        return (
            <ListGroup className="list-group">
                {plotData.map(p => (
                    <div
                        className={checkedPlots && checkedPlots.some(c => c.rowId === p.rowId) ? "plotRowStyle plot-row-selected" : "plotRowStyle"}
                        key={p.apNumber}>

                        <button type="button" id="plot-select-btn" className={checkedPlots && checkedPlots.some(c => c.rowId === p.rowId) ? "tractor-style tractor-selected" : "tractor-style"}
                            onClick={() => {
                                handleCheckboxSelection(p, data);
                            }}>
                            <Icon icon={ICONS.TRACTOR} color={checkedPlots && checkedPlots.some(c => c.rowId === p.rowId) ? "white" : "black"} size='20' />
                        </button>

                        <ListGroup.Item
                            className={checkedPlots && checkedPlots.some(c => c.rowId === p.rowId) ? "plotItemStyle plot-row-selected" : "plotItemStyle"}
                            /*className={"plotItemStyle"}*/
                            key={p.apNumber}
                            action onClick={(e) => { handleCheckboxSelection(p, data); }}>
                            <div><b> {p.name} - {p.apChar}</b></div>
                            <div>
                                {p.area} ha
                            </div>
                            <Badge pill bg='light' text='dark' className="badge-pill plot-assignment-count">{data.assignments.filter(a => a.plotId === p.apNumber).length}</Badge>
                            
                        </ListGroup.Item>
                    </div>
                ))}
            </ListGroup>
        );
    }

    /**
     * Checks if employee has any assignments. 
     * @param {any} objects Assignment objects in grouped by date.
     * @returns If there are no assignments, return a `Not found` message.
     */
    const emptyCheck = (objects) => {
        var empty = 0;
        objects.forEach(([date, items], i) => { if (date !== "null") empty++; });
        if (!empty) 
            return t("Assignments_Not_Found");
        return '';
    }

    const AssignmentListComponent = () => {
        return (
            <>
                {emptyCheck(Object.entries(assignments))}
                {Object.entries(assignments).map(([date, items], i) => (
                    <div key={date}>
                        {date !== "null" ? 
                            <>
                                {i === 0 && dayjs(date).format("DD.MM.YYYY") === dayjs().format("DD.MM.YYYY") ?
                                    <div className="assignments-today-section">
                                        <h4 className="assignments-today">{t('Today')} {dayjs(date).format("DD.MM.YYYY")}</h4>
                                        <AssignmentDateRow assignments={items} />
                                    </div>
                                    :
                                    <>
                                        <Accordion>
                                            <Accordion.Item>
                                                <Accordion.Header className="assignment-date-header"><h5>{dayjs(date).format("DD.MM.YYYY")}</h5></Accordion.Header>
                                                <Accordion.Body>
                                                    <AssignmentDateRow assignments={items} />
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </>}
                            </>
                            : ''}
                    </div>
                ))}
            </>
        );
    }

    const getActionAssignments = () => {
        return selectedAssignments;
    }

    const changeEmployee = (value) => {
        var isEmployee = value.value === 'all' ? false : true;
        handleAssignments(value.value, isEmployee, farmAssignments);
        setSelectedEmployee(value);
    }

    return (
        <>
            <Modal ref={modalBody} show={props.show} onHide={props.onHide} scrollable={true}>
                <Modal.Header closeButton className="top-shadow">
                    <Modal.Title className="text-dark">{t('JobAssignments')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ScrollableFX element={modalBody} refresh={scrollFx} style={{ maxHeight: 'calc(100vh)' }} />
                    <Form>
                        {loading || showActionModal ? <Spinner as="span" className="center" animation="border" role="status" variant="secondary" /> :
                            <>
                                {!isUserEmployee ?
                                    <>
                                        <Form.Label>{t('Employees')}</Form.Label>
                                        <Select className="employee-select" value={selectedEmployee} onChange={(value) => { changeEmployee(value); }} options={employees} />
                                    </>
                                    : ''}
                                <AssignmentListComponent />
                            </>}
                    </Form>
                </Modal.Body>   
                <Modal.Footer className={"modal-footer-right-align bottom-shadow"}>
                    <Button variant="outline-secondary" onClick={props.onHide}>{t('Close')}</Button>
                    <Button variant="primary" className="btn-show-map" disabled={!checkedPlots.length} onClick={() => {
                        setMapModalShow(true);
                    }}>{t('ShowOnMap')}</Button>
                    {buttonWriteShow === true ?
                        <Button variant="success" className="btn-show-more" disabled={!checkedPlots.length} onClick={() => {
                            setShowActionModal(true);
                        }}>{t('AddPlotAction')}</Button>
                        : ''}
                </Modal.Footer>

                <ActionSelectionModal
                    show={showActionModal}
                    onHide={() => {
                        setShowActionModal(false);
                        if (refresh) {
                            loadModalData();
                        }
                    }}
                    selectedplots={checkedPlots ? plots.filter(p => checkedPlots.find(c => c.plotId === p.apNumber)) : []}
                    assignments={getActionAssignments()}
                    assignmentMode={true}
                    addMode={false}
                    refreshAssignments={() => { setRefresh(true); }}
                />

                {mapModalShow ? 
                    <MapModal
                        show={mapModalShow}
                        assignments={getActionAssignments()}
                        openActionSelection={() => {
                            setMapModalShow(false);
                            setShowActionModal(true);
                        }}
                        onHide={() => {
                            setMapModalShow(false);
                        }}
                    />
                    : ''}
            </Modal>
        </>
    )
}

export default AssignmentsModal;